import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CountryPhoneCode } from '../_models/models';
import { COUNTRY_PHONE_CODES, findCountryByCode } from '../_components/phone-input/config/phone-input.config';

@Injectable({
  providedIn: 'root'
})
export class PhoneValidationService {
  /**
   * Formats a phone number with the given ISO and country/dial code.
   * @param isoCode The ISO country code (e.g., "US")
   * @param phone The phone number to format
   * @param countryOrDialCode The country code (ISO or dial code like "+1") to derive dial code
   * @returns Formatted phone number (e.g., "US+1-1234567890")
   */
  formatPhoneWithCountryCode(isoCode: string, phone: string): string {
    if (!isoCode || !phone) {
      return '';
    }

    let phoneDigits = phone.trim().replace(/^\+/, '');
    if (!/^\d+$/.test(phoneDigits)) {
      phoneDigits = phoneDigits.replace(/\D/g, '');
      if (!phoneDigits) {
        return '';
      }
    }

    let actualDialCode: string;
    if (/^[A-Za-z]{2,3}$/.test(isoCode)) {
      const found = COUNTRY_PHONE_CODES.find(c => c.iso === isoCode.toUpperCase());
      actualDialCode = found ? found.countryCode : '';
    } else {
      actualDialCode = isoCode;
    }
    if (!actualDialCode) return '';

    const formattedDialCode = actualDialCode.startsWith('+') ? actualDialCode : `+${actualDialCode}`;

    return `${isoCode.toUpperCase()}${formattedDialCode}-${phoneDigits}`;
  }

  /**
   * Extracts ISO, country code, and phone number from a formatted phone string.
   * @param formattedPhone The formatted phone number (e.g., "US+1-1234567890")
   * @returns Object with iso, countryCode, and phoneNumber
   */
  extractCountryCodeFromPhone(formattedPhone: string): { iso: string; countryCode: string; phoneNumber: string } {
    const defaultResult = { iso: 'US', countryCode: '+1', phoneNumber: '' };
    if (!formattedPhone) {
      return defaultResult;
    }

    const partsRegex = /^([A-Za-z]{2,3})(\+\d{1,4})[\s-]*(.+)$/;
    const match = formattedPhone.match(partsRegex);

    if (match && match[1] && match[2] && match[3]) {
      const iso = match[1].toUpperCase();
      const countryCode = match[2];
      const phoneNumber = match[3].trim().replace(/\D/g, '');

      const countryConfig = COUNTRY_PHONE_CODES.find(c => c.iso === iso && c.countryCode === countryCode);
      if (countryConfig) {
        return { iso, countryCode, phoneNumber };
      }

      const foundByIso = COUNTRY_PHONE_CODES.find(c => c.iso === iso);
      if (foundByIso) {
        return { iso, countryCode: foundByIso.countryCode, phoneNumber };
      }
    }

    const oldFormatRegex = /^\+(\d{1,4})[\s-]+(.+)$/;
    const oldMatch = formattedPhone.match(oldFormatRegex);
    if (oldMatch && oldMatch[1] && oldMatch[2]) {
      const dialCode = `+${oldMatch[1]}`;
      const phoneNum = oldMatch[2].trim().replace(/\D/g, '');
      const country = COUNTRY_PHONE_CODES.find(c => c.countryCode === dialCode);
      return { iso: country ? country.iso : 'US', countryCode: dialCode, phoneNumber: phoneNum };
    }

    return { ...defaultResult, phoneNumber: formattedPhone.replace(/^([A-Za-z]{2,3})?\+?/, '').trim().replace(/\D/g, '') };
  }

  /**
   * Creates a validator function to validate phone number length based on country code
   * @param countryCode The country code string (e.g., '+1')
   * @returns A validator function
   */
  phoneNumberLengthValidator(countryCode: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !control.value) {
        return null;
      }

      const phone = control.value.toString().trim();
      if (!phone) return null;

      if (!countryCode) {
        return null;
      }

      const validation = this.validatePhoneNumberLength(phone, countryCode);

      if (!validation.isValid) {
        if (validation.isTooShort) {
          return { tooShort: true };
        }
        if (validation.isTooLong) {
          return { tooLong: true };
        }
        if (validation.invalidPhone) {
          return { invalidPhone: true };
        }
        return { unknownValidationError: true };
      }

      return null;
    };
  }

  /**
   * Returns a list of all supported country phone codes
   * @returns Array of CountryPhoneCode objects
   */
  getCountryPhoneCodes(): CountryPhoneCode[] {
    return COUNTRY_PHONE_CODES;
  }

  /**
   * Validates the phone number length based on the country code
   * @param phone The phone number to validate
   * @param countryCode The country code to validate against
   * @returns Validation result
   * @private Used internally by the validation service
   */
  private validatePhoneNumberLength(phone: string, countryCode: string): {
    isValid: boolean;
    isTooShort?: boolean;
    isTooLong?: boolean;
    invalidPhone?: boolean;
  } {
    if (!phone || !countryCode) {
      return { isValid: false };
    }

    const country = findCountryByCode(countryCode);

    if (!country) {
      return { isValid: false };
    }

    const lengthsToUse = Array.isArray(country.validLengths) && country.validLengths.length > 0
      ? country.validLengths
      : (typeof country.phoneLength === 'number' ? [country.phoneLength] : null);

    if (!lengthsToUse) {
      return { isValid: false };
    }

    const phoneLength = phone.length;

    if (lengthsToUse.includes(phoneLength)) {
      return { isValid: true };
    }

    const minAllowedLength = Math.min(...lengthsToUse);
    if (phoneLength < minAllowedLength) {
      return { isValid: false, isTooShort: true };
    }

    const maxAllowedLength = Math.max(...lengthsToUse);
    if (phoneLength > maxAllowedLength) {
      return { isValid: false, isTooLong: true };
    }

    return { isValid: false, invalidPhone: true };
  }
}
